import { Suspense, useMemo } from 'react';
import { Avatar, Card, CardContent, Skeleton, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatNumber } from '@/utils/formatters';

const PortfolioAnalysisServiceCard = (props) => {
  const {
    disabled = false,
    name,
    label = name,
    path,
    value,
    unit,
    area,
    buildingCount,
    icon,
    color,
    summedData,
  } = props;
  const { t } = useTranslation('portfolio-analysis_dashboard');
  const content = (
    <CardContent>
      <Box
        flexDirection={'row'}
        display={'flex'}
        justifyContent={'space-between'}
      >
        <Box>
          <Box
            sx={{
              display: 'flex',

              flexDirection: 'row',
            }}
          >
            <Typography variant="body1" color="textSecondary" fontWeight="bold">
              {label}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {disabled ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                }}
              >
                <Typography variant="b1" color="textSecondary">
                  {t('cards.unavailable')}
                </Typography>
              </Box>
            ) : (
              <Box display={'flex'} flexDirection={'column'}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                  }}
                >
                  <Typography
                    variant="h4"
                    color="textPrimary"
                    fontWeight={'bold'}
                  >
                    {formatNumber(value, 0)} {unit}
                  </Typography>
                </Box>

                <Box display={'flex'} flexDirection={'column'} mt={1}>
                  <Typography variant="body2" color="textSecondary">
                    {t('cards.buildings.area')}: {formatNumber(area)}{' '}
                    {t('cards.buildings.unit')}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {t('cards.buildings.count')}: {buildingCount}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
        <Box>
          <Avatar sx={{ bgcolor: color, width: 56, height: 56 }}>{icon}</Avatar>
        </Box>
      </Box>
      {summedData && (
        <SparklineWrapper dataPoints={summedData} unit={unit} color={color} />
      )}
    </CardContent>
  );

  return (
    <Suspense fallback={<Skeleton />}>
      <Card variant="outlined" sx={{ height: '100%' }}>
        {path && !disabled ? <Link to={`${path}`}>{content}</Link> : content}
      </Card>
    </Suspense>
  );
};

import { SparkLineChart } from '@mui/x-charts/SparkLineChart';
import { areaElementClasses } from '@mui/x-charts/LineChart';
import { DateTime } from 'luxon';

// Function to group data by day and sum values
const groupDataByWeek = (data) => {
  if (!data || data.length === 0) return [];
  const grouped = data.reduce((acc, { timestamp, value }) => {
    const week = DateTime.fromISO(timestamp)
      .startOf('week')
      .toFormat("yyyy-'W'WW"); // Group by week (ISO week)
    acc[week] = (acc[week] || 0) + value; // Sum values for the same day
    return acc;
  }, {});
  return Object.entries(grouped).map(([date, value]) => ({
    timestamp: date, // Keep the day as the timestamp
    value,
  }));
};

const AreaGradient = ({ color, id }) => (
  <defs>
    <linearGradient id={id} x1="50%" y1="0%" x2="50%" y2="100%">
      <stop offset="0%" stopColor={color} stopOpacity={0.3} />
      <stop offset="100%" stopColor={color} stopOpacity={0} />
    </linearGradient>
  </defs>
);

const SparklineWrapper = ({ dataPoints, unit, color }) => {
  // Group data by day and then select representative points
  const groupedData = useMemo(() => groupDataByWeek(dataPoints), [dataPoints]);

  const sparklineData = groupedData
    .map((entry) => parseInt(entry.value))
    .filter((entry) => entry);
  const tooltipLabels = groupedData.map((entry) => {
    const start = DateTime.fromISO(entry.timestamp)
      .setZone('Europe/Copenhagen')
      .setLocale('da-dk');
    const end = start.endOf('week');
    const weekNumber = start.weekNumber;
    return `Uge ${weekNumber}: ${start.toFormat('dd MMM yy')} - ${end.toFormat(
      'dd MMM yy',
    )}`;
  });
  const gradientId = `sparkline-gradient-${color}`;
  const settings = {
    valueFormatter: (value) => `${formatNumber(value)} ${unit}`,
    // height: 100,
    // showTooltip: true,
    // showHighlight: true,
  };
  return (
    <Box sx={{ width: '100%', display: 'flex' }}>
      <SparkLineChart
        data={sparklineData.slice(1, -1)}
        height={50}
        colors={[color]} // Green color for the line
        showTooltip
        showHighlight
        xAxis={{
          scaleType: 'band', // Required for tooltips to work properly
          data: tooltipLabels.slice(1, sparklineData.length - 1),
        }}
        area={true} // Enables the filled area below the line
        curve="monotoneX" // Smooths out the line curve
        {...settings}
        sx={{
          [`& .${areaElementClasses.root}`]: {
            fill: `url(#${gradientId})`,
            stroke: 'none',
          },
        }}
      >
        <AreaGradient color={color} id={gradientId} />
      </SparkLineChart>
    </Box>
  );
};

export default PortfolioAnalysisServiceCard;
